import { cn } from "@gettactic/helpers";
import React from "react";

export interface EditorFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Short leading key shown inside the field (e.g. "X", "W", "px"). */
  fieldKey?: React.ReactNode;
  /** Accessible name for the input. Required: fields have no visible label. */
  label: string;
  containerClassName?: string;
}

/**
 * The single inspector input: 28px bordered field with an optional leading
 * key, accent focus ring and tabular numerals. Replaces the raw
 * `<input className="rounded border border-gray-300 …">` copies.
 */
export const EditorField = React.forwardRef<HTMLInputElement, EditorFieldProps>(
  ({ fieldKey, label, className, containerClassName, ...rest }, ref) => {
    return (
      <label
        className={cn(
          "flex h-7 items-center gap-1.5 rounded-md border border-neutral-200 bg-background-primary px-2 transition-colors",
          "focus-within:border-blue-500 hover:border-neutral-300 focus-within:hover:border-blue-500",
          containerClassName
        )}
      >
        {fieldKey !== undefined ? (
          <span className="min-w-3 flex-none text-[11px] text-neutral-400">
            {fieldKey}
          </span>
        ) : undefined}
        <input
          ref={ref}
          aria-label={label}
          className={cn(
            "w-full min-w-0 border-none bg-transparent p-0 text-neutral-900 text-xs outline-none [font-variant-numeric:tabular-nums] placeholder:text-neutral-400",
            className
          )}
          {...rest}
        />
      </label>
    );
  }
);
EditorField.displayName = "EditorField";

/** Two-column grid for compact field pairs (X/Y, W/H). */
export const EditorFieldGrid: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ className, children }) => (
  <div className={cn("grid grid-cols-2 gap-1.5", className)}>{children}</div>
);

/** A labelled row: text on the left, control on the right. */
export const EditorFieldRow: React.FC<
  React.PropsWithChildren<{ label: React.ReactNode; className?: string }>
> = ({ label, className, children }) => (
  <div className={cn("flex min-h-7 items-center gap-2", className)}>
    <span className="flex-1 truncate text-neutral-600 text-xs">{label}</span>
    <div className="flex flex-none items-center gap-1.5">{children}</div>
  </div>
);
